import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { recoveryAPI } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart, faArrowLeft, faCheck, faCalendar, faClock,
  faFire, faTrophy, faSmile, faMeh, faFrown, faTimes,
  faPause, faPen, faLightbulb, faLeaf
} from '@fortawesome/free-solid-svg-icons';
import { getUser } from '../utils/auth';
import 'react-toastify/dist/ReactToastify.css';

// Recovery type configurations
const RECOVERY_TYPES = [
  { 
    value: 'addiction', 
    label: 'Addiction', 
    emoji: '🚭',
    description: 'Support for overcoming habits you want to reduce or stop'
  },
  { 
    value: 'work_stress', 
    label: 'Work stress', 
    emoji: '💼',
    description: 'Recover from work-related pressure and overwhelm'
  },
  { 
    value: 'academic_stress', 
    label: 'Academic stress', 
    emoji: '📚',
    description: 'Support for managing educational pressure'
  },
  { 
    value: 'loss', 
    label: 'Loss', 
    emoji: '🕊️',
    description: 'Healing through grief and remembering'
  },
  { 
    value: 'heartbreak', 
    label: 'Heartbreak', 
    emoji: '💔',
    description: 'Recovery from relationship endings'
  },
];

// Addiction habit types
const ADDICTION_HABITS = [
  'Drugs', 'Smoking', 'Alcohol', 'Gaming', 
  'Social media', 'Junk food', 'Porn', 'Gambling'
];

// Work stress contributors
const WORK_CONTRIBUTORS = [
  'Too many tasks', 'Mental exhaustion', 'Emotional pressure',
  'Time pressure', 'Expectations', 'Unclear boundaries'
];

// Academic feelings
const ACADEMIC_FEELINGS = [
  'Focused', 'Mentally tired', 'Overloaded', 'Avoidant'
];

// Loss emotions
const LOSS_EMOTIONS = [
  'Missing', 'Sadness', 'Anger', 'Guilt', 'Confusion', 'Calm', 'Numb'
];

// Heartbreak feelings
const HEARTBREAK_FEELINGS = [
  'Heavy', 'Tender', 'Numb', 'Calm', 'Overwhelmed'
];

const Recovery = () => {
  const navigate = useNavigate();
  const user = getUser();

  // Navigation state
  const [currentScreen, setCurrentScreen] = useState('welcome');
  
  // Recovery setup state
  const [selectedType, setSelectedType] = useState('');
  const [addictionHabit, setAddictionHabit] = useState('');
  const [customHabit, setCustomHabit] = useState('');
  
  // Active recovery state
  const [activeRecovery, setActiveRecovery] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check-in state
  const [checkInData, setCheckInData] = useState({
    // Addiction
    struggled: null,
    reflection: '',
    reflectionQuestion: '',
    supportAction: '',
    
    // Work stress
    workFeeling: '',
    workContributor: '',
    workActions: [],
    
    // Academic
    academicFeeling: '',
    academicReflection: '',
    academicActions: [],
    
    // Loss
    lossEmotion: '',
    lossReflection: '',
    lossAction: '',
    
    // Heartbreak
    heartFeeling: '',
    heartReflection: '',
    heartAction: ''
  });
  
  const [todayCheckIn, setTodayCheckIn] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [stats, setStats] = useState({ daysActive: 0, totalCheckIns: 0, streak: 0 });

  useEffect(() => {
    fetchRecoveryData();
  }, []);

  const fetchRecoveryData = async () => {
    try {
      setLoading(true);
      
      const statusResponse = await recoveryAPI.getStatus();
      if (statusResponse.data.is_active) {
        setActiveRecovery(statusResponse.data.recovery);
        setCurrentScreen('check_in');
        
        const checkInsResponse = await recoveryAPI.getCheckIns(30);
        setCheckIns(checkInsResponse.data?.check_ins || []);
        
        // Check if checked in today
        const today = new Date().toISOString().split('T')[0];
        const todaysCheckIn = checkInsResponse.data?.check_ins?.find(
          ci => ci.check_in_date === today
        );
        setTodayCheckIn(todaysCheckIn);
        
        calculateStats(statusResponse.data.recovery, checkInsResponse.data?.check_ins || []);
      } else {
        setCurrentScreen('welcome');
      }
    } catch (error) {
      console.error('Error fetching recovery data:', error);
      setCurrentScreen('welcome');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (recovery, checkInsArray) => {
    if (!recovery || !recovery.started_at) {
      setStats({ daysActive: 0, totalCheckIns: 0, streak: 0 });
      return;
    }

    const startDate = new Date(recovery.started_at);
    const today = new Date();
    const daysActive = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
    const totalCheckIns = checkInsArray.length;

    // Calculate streak
    let streak = 0;
    const sortedCheckIns = [...checkInsArray].sort((a, b) => 
      new Date(b.check_in_date) - new Date(a.check_in_date)
    );
    
    for (let i = 0; i < sortedCheckIns.length; i++) {
      const checkInDate = new Date(sortedCheckIns[i].check_in_date);
      const expectedDate = new Date(today);
      expectedDate.setDate(expectedDate.getDate() - i);
      
      if (checkInDate.toISOString().split('T')[0] === expectedDate.toISOString().split('T')[0]) {
        streak++;
      } else {
        break;
      }
    }

    setStats({ daysActive, totalCheckIns, streak });
  };

  const handleActivateRecovery = async () => {
    try {
      setLoading(true);
      
      let specificFocus = '';
      if (selectedType === 'addiction') {
        specificFocus = addictionHabit === 'Custom' ? customHabit : addictionHabit;
      }
      
      await recoveryAPI.activate({
        recovery_type: selectedType,
        specific_focus: specificFocus
      });
      
      toast.success('Recovery journey activated!');
      await fetchRecoveryData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to activate recovery');
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to end your recovery journey?')) return;

    try {
      await recoveryAPI.deactivate();
      toast.info('Recovery journey ended');
      setActiveRecovery(null);
      setCheckIns([]);
      setTodayCheckIn(null);
      setCurrentScreen('welcome');
    } catch (error) {
      toast.error('Failed to deactivate recovery');
    }
  };

  const handleSubmitCheckIn = async () => {
    try {
      setLoading(true);
      
      // Build check-in payload
      let payload = {
        response: 'completed'
      };
      
      if (activeRecovery.recovery_type === 'addiction') {
        payload.reflection = JSON.stringify({
          struggled: checkInData.struggled,
          reflectionQuestion: checkInData.reflectionQuestion,
          reflection: checkInData.reflection,
          supportAction: checkInData.supportAction
        });
        payload.action_taken = checkInData.supportAction;
      } else if (activeRecovery.recovery_type === 'work_stress') {
        payload.reflection = JSON.stringify({
          feeling: checkInData.workFeeling,
          contributor: checkInData.workContributor,
          actions: checkInData.workActions
        });
        payload.action_taken = checkInData.workActions.join(', ');
      } else if (activeRecovery.recovery_type === 'academic_stress') {
        payload.reflection = JSON.stringify({
          feeling: checkInData.academicFeeling,
          reflectionQuestion: checkInData.academicReflection,
          actions: checkInData.academicActions
        });
        payload.action_taken = checkInData.academicActions.join(', ');
      } else if (activeRecovery.recovery_type === 'loss') {
        payload.reflection = JSON.stringify({
          emotion: checkInData.lossEmotion,
          reflectionQuestion: checkInData.lossReflection,
          action: checkInData.lossAction
        });
        payload.action_taken = checkInData.lossAction;
      } else if (activeRecovery.recovery_type === 'heartbreak') {
        payload.reflection = JSON.stringify({
          feeling: checkInData.heartFeeling,
          reflectionQuestion: checkInData.heartReflection,
          action: checkInData.heartAction
        });
        payload.action_taken = checkInData.heartAction;
      }
      
      await recoveryAPI.checkIn(payload);
      
      toast.success('Check-in recorded!');
      setCurrentScreen('summary');
      await fetchRecoveryData();
      
      // Reset check-in data
      setCheckInData({
        struggled: null,
        reflection: '',
        reflectionQuestion: '',
        supportAction: '',
        workFeeling: '',
        workContributor: '',
        workActions: [],
        academicFeeling: '',
        academicReflection: '',
        academicActions: [],
        lossEmotion: '',
        lossReflection: '',
        lossAction: '',
        heartFeeling: '',
        heartReflection: '',
        heartAction: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record check-in');
    } finally {
      setLoading(false);
    }
  };

  const renderWelcomeScreen = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
      <div style={{ fontSize: '4rem', marginBottom: '2rem' }}>🌱</div>
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#FFB6C1' }}>
        Recovery space
      </h1>
      <p style={{ color: '#ccc', lineHeight: '1.8', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
        This space is here to support you through difficult seasons.
      </p>
      <p style={{ color: '#ccc', lineHeight: '1.8', marginBottom: '2rem', fontSize: '1.1rem' }}>
        You don't need to be strong here. You don't need to explain yourself.
      </p>
      <button
        onClick={() => setCurrentScreen('select_type')}
        style={{
          padding: '1rem 3rem',
          backgroundColor: '#FFB6C1',
          border: 'none',
          borderRadius: '12px',
          color: '#000',
          fontSize: '1.1rem',
          fontWeight: '600',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(255, 182, 193, 0.3)'
        }}
      >
        Continue
      </button>
    </div>
  );

  const renderSelectTypeScreen = () => (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.8rem', marginBottom: '1rem', color: '#FFB6C1', textAlign: 'center' }}>
        What are you recovering from right now?
      </h2>
      
      <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
        {RECOVERY_TYPES.map((type) => (
          <div
            key={type.value}
            onClick={() => {
              setSelectedType(type.value);
              if (type.value === 'addiction') {
                setCurrentScreen('addiction_habit');
              } else {
                handleActivateRecovery();
              }
            }}
            style={{
              backgroundColor: '#1a1a1a',
              border: '2px solid #333',
              borderRadius: '12px',
              padding: '1.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#FFB6C1';
              e.currentTarget.style.backgroundColor = '#252525';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#333';
              e.currentTarget.style.backgroundColor = '#1a1a1a';
            }}
          >
            <div style={{ fontSize: '2.5rem' }}>{type.emoji}</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ color: '#FFB6C1', marginBottom: '0.25rem' }}>{type.label}</h3>
              <p style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>{type.description}</p>
            </div>
          </div>
        ))}
      </div>
      
      <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
        You can change this anytime. Healing isn't one-directional.
      </p>
    </div>
  );

  const renderAddictionHabitScreen = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.8rem', marginBottom: '1rem', color: '#FFB6C1' }}>
        What habit are you trying to reduce or stop?
      </h2>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {ADDICTION_HABITS.map((habit) => (
          <button
            key={habit}
            onClick={() => {
              setAddictionHabit(habit);
              setCustomHabit('');
            }}
            style={{
              padding: '1rem',
              backgroundColor: addictionHabit === habit ? '#FFB6C1' : '#1a1a1a',
              border: `2px solid ${addictionHabit === habit ? '#FFB6C1' : '#333'}`,
              borderRadius: '8px',
              color: addictionHabit === habit ? '#000' : '#fff',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            {habit}
          </button>
        ))}
        <button
          onClick={() => {
            setAddictionHabit('Custom');
          }}
          style={{
            padding: '1rem',
            backgroundColor: addictionHabit === 'Custom' ? '#FFB6C1' : '#1a1a1a',
            border: `2px solid ${addictionHabit === 'Custom' ? '#FFB6C1' : '#333'}`,
            borderRadius: '8px',
            color: addictionHabit === 'Custom' ? '#000' : '#fff',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem',
            gridColumn: 'span 2'
          }}
        >
          Custom (type your own)
        </button>
      </div>
      
      {addictionHabit === 'Custom' && (
        <input
          type="text"
          value={customHabit}
          onChange={(e) => setCustomHabit(e.target.value)}
          placeholder="Enter your habit..."
          style={{
            width: '100%',
            padding: '1rem',
            backgroundColor: '#0a0a0a',
            border: '1px solid #333',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '1rem',
            marginBottom: '2rem'
          }}
        />
      )}
      
      <button
        onClick={handleActivateRecovery}
        disabled={!addictionHabit || (addictionHabit === 'Custom' && !customHabit.trim())}
        style={{
          width: '100%',
          padding: '1rem',
          backgroundColor: addictionHabit && (addictionHabit !== 'Custom' || customHabit.trim()) ? '#FFB6C1' : '#333',
          border: 'none',
          borderRadius: '8px',
          color: addictionHabit && (addictionHabit !== 'Custom' || customHabit.trim()) ? '#000' : '#666',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: addictionHabit && (addictionHabit !== 'Custom' || customHabit.trim()) ? 'pointer' : 'not-allowed'
        }}
      >
        Continue
      </button>
    </div>
  );

  const renderAddictionCheckIn = () => {
    const reflectionQuestions = checkInData.struggled ? [
      'What were you feeling before it happened?',
      'What were you hoping this habit would give you?',
      'What made today harder than usual?',
      'What felt overwhelming today?'
    ] : [
      'What helped you today?',
      'What felt different compared to harder days?',
      'What would you like to remember from today?'
    ];

    const supportActions = checkInData.struggled ? [
      'Take a slow pause now',
      'Write one sentence about how you feel',
      'Ask the system to keep tomorrow light',
      'Skip tasks for the rest of today'
    ] : [
      'Write a note to yourself',
      'Mark today as a steady day',
      'Keep the same support tomorrow'
    ];

    return (
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '1.8rem', marginBottom: '1.5rem', color: '#FFB6C1' }}>
          How did today go with this habit?
        </h2>
        
        {checkInData.struggled === null ? (
          <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
            <button
              onClick={() => setCheckInData({ ...checkInData, struggled: true })}
              style={{
                padding: '2rem',
                backgroundColor: '#1a1a1a',
                border: '2px solid #ef4444',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: '600'
              }}
            >
              I struggled today
            </button>
            <button
              onClick={() => setCheckInData({ ...checkInData, struggled: false })}
              style={{
                padding: '2rem',
                backgroundColor: '#1a1a1a',
                border: '2px solid #4ade80',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: '600'
              }}
            >
              I managed today
            </button>
          </div>
        ) : (
          <>
            <div style={{
              backgroundColor: checkInData.struggled ? '#ef44441a' : '#4ade801a',
              border: `1px solid ${checkInData.struggled ? '#ef4444' : '#4ade80'}`,
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <p style={{ color: '#ccc', lineHeight: '1.6', marginBottom: '1rem' }}>
                {checkInData.struggled 
                  ? 'Thank you for being honest. Struggle does not cancel progress.'
                  : 'That took effort. It matters.'}
              </p>
            </div>

            {!checkInData.reflectionQuestion ? (
              <>
                <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
                  Reflection (choose one)
                </h3>
                <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                  {reflectionQuestions.map((question) => (
                    <button
                      key={question}
                      onClick={() => setCheckInData({ ...checkInData, reflectionQuestion: question })}
                      style={{
                        padding: '1rem',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '0.95rem'
                      }}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#FFB6C1', marginBottom: '0.5rem' }}>
                    {checkInData.reflectionQuestion}
                  </label>
                  <textarea
                    value={checkInData.reflection}
                    onChange={(e) => setCheckInData({ ...checkInData, reflection: e.target.value })}
                    placeholder="Your thoughts..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {!checkInData.supportAction ? (
                  <>
                    <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
                      {checkInData.struggled ? 'Gentle Support Action (choose one)' : 'Reinforcement Action (choose one)'}
                    </h3>
                    <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                      {supportActions.map((action) => (
                        <button
                          key={action}
                          onClick={() => setCheckInData({ ...checkInData, supportAction: action })}
                          style={{
                            padding: '1rem',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: '8px',
                            color: '#fff',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.95rem'
                          }}
                        >
                          {action}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #FFB6C1',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '2rem'
                    }}>
                      <p style={{ color: '#FFB6C1', fontSize: '0.95rem' }}>
                        Action: {checkInData.supportAction}
                      </p>
                    </div>

                    <div style={{
                      backgroundColor: '#FFB6C11a',
                      border: '1px solid #FFB6C1',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      marginBottom: '2rem',
                      textAlign: 'center'
                    }}>
                      <p style={{ color: '#FFB6C1', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                        {checkInData.struggled 
                          ? 'Today was about getting through, not getting it right.'
                          : 'You supported yourself today.'}
                      </p>
                    </div>

                    <button
                      onClick={handleSubmitCheckIn}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        backgroundColor: '#FFB6C1',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#000',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1
                      }}
                    >
                      {loading ? 'Saving...' : 'Complete Check-in'}
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  const renderWorkStressCheckIn = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.8rem', marginBottom: '1.5rem', color: '#FFB6C1' }}>
        How did work affect you today?
      </h2>
      
      {!checkInData.workFeeling ? (
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {['It felt manageable', 'It drained me', 'It overwhelmed me'].map((feeling) => (
            <button
              key={feeling}
              onClick={() => setCheckInData({ ...checkInData, workFeeling: feeling })}
              style={{
                padding: '1.5rem',
                backgroundColor: '#1a1a1a',
                border: '2px solid #333',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}
            >
              {feeling}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #FFB6C1',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <p style={{ color: '#FFB6C1' }}>Feeling: {checkInData.workFeeling}</p>
          </div>

          {!checkInData.workContributor ? (
            <>
              <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
                What contributed most to how work felt today?
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                {WORK_CONTRIBUTORS.map((contributor) => (
                  <button
                    key={contributor}
                    onClick={() => setCheckInData({ ...checkInData, workContributor: contributor })}
                    style={{
                      padding: '1rem',
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    {contributor}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #FFB6C1',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '2rem'
              }}>
                <p style={{ color: '#FFB6C1' }}>Main contributor: {checkInData.workContributor}</p>
              </div>

              <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
                Healing Actions (choose any)
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                {['Reduce tomorrow\'s workload', 'Limit heavy tasks', 'Slow reminders', 'Take a work pause', 'End today early'].map((action) => {
                  const isSelected = checkInData.workActions.includes(action);
                  return (
                    <button
                      key={action}
                      onClick={() => {
                        setCheckInData({
                          ...checkInData,
                          workActions: isSelected
                            ? checkInData.workActions.filter(a => a !== action)
                            : [...checkInData.workActions, action]
                        });
                      }}
                      style={{
                        padding: '1rem',
                        backgroundColor: isSelected ? '#FFB6C1' : '#1a1a1a',
                        border: `1px solid ${isSelected ? '#FFB6C1' : '#333'}`,
                        borderRadius: '8px',
                        color: isSelected ? '#000' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {isSelected && <FontAwesomeIcon icon={faCheck} />}
                      {action}
                    </button>
                  );
                })}
              </div>

              <div style={{
                backgroundColor: '#FFB6C11a',
                border: '1px solid #FFB6C1',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem',
                textAlign: 'center'
              }}>
                <p style={{ color: '#FFB6C1', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                  Rest is part of sustainable work.
                </p>
              </div>

              <button
                onClick={handleSubmitCheckIn}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: '#FFB6C1',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Saving...' : 'Complete Check-in'}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );

  const renderAcademicCheckIn = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.8rem', marginBottom: '1.5rem', color: '#FFB6C1' }}>
        How did studying or school feel today?
      </h2>
      
      {!checkInData.academicFeeling ? (
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {ACADEMIC_FEELINGS.map((feeling) => (
            <button
              key={feeling}
              onClick={() => setCheckInData({ ...checkInData, academicFeeling: feeling })}
              style={{
                padding: '1.5rem',
                backgroundColor: '#1a1a1a',
                border: '2px solid #333',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}
            >
              {feeling}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #FFB6C1',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <p style={{ color: '#FFB6C1' }}>Feeling: {checkInData.academicFeeling}</p>
          </div>

          <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
            Reflection (choose one)
          </h3>
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {['What made it hard to focus today?', 'What helped even a little?', 'What felt mentally heavy?'].map((question) => (
              <button
                key={question}
                onClick={() => setCheckInData({ ...checkInData, academicReflection: question })}
                style={{
                  padding: '1rem',
                  backgroundColor: checkInData.academicReflection === question ? '#FFB6C1' : '#1a1a1a',
                  border: `1px solid ${checkInData.academicReflection === question ? '#FFB6C1' : '#333'}`,
                  borderRadius: '8px',
                  color: checkInData.academicReflection === question ? '#000' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {question}
              </button>
            ))}
          </div>

          {checkInData.academicReflection && (
            <>
              <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
                Support Actions
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                {['Reduce study load', 'Break tasks into smaller parts', 'Keep tomorrow gentle', 'Pause deadlines for now'].map((action) => {
                  const isSelected = checkInData.academicActions.includes(action);
                  return (
                    <button
                      key={action}
                      onClick={() => {
                        setCheckInData({
                          ...checkInData,
                          academicActions: isSelected
                            ? checkInData.academicActions.filter(a => a !== action)
                            : [...checkInData.academicActions, action]
                        });
                      }}
                      style={{
                        padding: '1rem',
                        backgroundColor: isSelected ? '#FFB6C1' : '#1a1a1a',
                        border: `1px solid ${isSelected ? '#FFB6C1' : '#333'}`,
                        borderRadius: '8px',
                        color: isSelected ? '#000' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {isSelected && <FontAwesomeIcon icon={faCheck} />}
                      {action}
                    </button>
                  );
                })}
              </div>

              <div style={{
                backgroundColor: '#FFB6C11a',
                border: '1px solid #FFB6C1',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem',
                textAlign: 'center'
              }}>
                <p style={{ color: '#FFB6C1', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                  Learning improves when pressure drops.
                </p>
              </div>

              <button
                onClick={handleSubmitCheckIn}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: '#FFB6C1',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Saving...' : 'Complete Check-in'}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );

  const renderLossCheckIn = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.8rem', marginBottom: '1.5rem', color: '#FFB6C1' }}>
        What did today bring up for you?
      </h2>
      
      {!checkInData.lossEmotion ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {LOSS_EMOTIONS.map((emotion) => (
            <button
              key={emotion}
              onClick={() => setCheckInData({ ...checkInData, lossEmotion: emotion })}
              style={{
                padding: '1.5rem',
                backgroundColor: '#1a1a1a',
                border: '2px solid #333',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}
            >
              {emotion}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #FFB6C1',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <p style={{ color: '#FFB6C1' }}>Emotion: {checkInData.lossEmotion}</p>
          </div>

          <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
            Supportive Reflection
          </h3>
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {['What did you miss today?', 'What reminded you of what you lost?', 'What helped you get through today?'].map((question) => (
              <button
                key={question}
                onClick={() => setCheckInData({ ...checkInData, lossReflection: question })}
                style={{
                  padding: '1rem',
                  backgroundColor: checkInData.lossReflection === question ? '#FFB6C1' : '#1a1a1a',
                  border: `1px solid ${checkInData.lossReflection === question ? '#FFB6C1' : '#333'}`,
                  borderRadius: '8px',
                  color: checkInData.lossReflection === question ? '#000' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {question}
              </button>
            ))}
          </div>

          {checkInData.lossReflection && (
            <>
              <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
                Healing Actions
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                {['Write a memory', 'Write how you feel right now', 'Sit quietly with this space', 'Keep tomorrow light'].map((action) => (
                  <button
                    key={action}
                    onClick={() => setCheckInData({ ...checkInData, lossAction: action })}
                    style={{
                      padding: '1rem',
                      backgroundColor: checkInData.lossAction === action ? '#FFB6C1' : '#1a1a1a',
                      border: `1px solid ${checkInData.lossAction === action ? '#FFB6C1' : '#333'}`,
                      borderRadius: '8px',
                      color: checkInData.lossAction === action ? '#000' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    {action}
                  </button>
                ))}
              </div>

              {checkInData.lossAction && (
                <>
                  <div style={{
                    backgroundColor: '#FFB6C11a',
                    border: '1px solid #FFB6C1',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '2rem',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: '#FFB6C1', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                      There is no correct way to grieve.
                    </p>
                  </div>

                  <button
                    onClick={handleSubmitCheckIn}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      backgroundColor: '#FFB6C1',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#000',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Saving...' : 'Complete Check-in'}
                  </button>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );

  const renderHeartbreakCheckIn = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.8rem', marginBottom: '1.5rem', color: '#FFB6C1' }}>
        How did your heart feel today?
      </h2>
      
      {!checkInData.heartFeeling ? (
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {HEARTBREAK_FEELINGS.map((feeling) => (
            <button
              key={feeling}
              onClick={() => setCheckInData({ ...checkInData, heartFeeling: feeling })}
              style={{
                padding: '1.5rem',
                backgroundColor: '#1a1a1a',
                border: '2px solid #333',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}
            >
              {feeling}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #FFB6C1',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <p style={{ color: '#FFB6C1' }}>Feeling: {checkInData.heartFeeling}</p>
          </div>

          <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
            Reflection Options
          </h3>
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {['What moment brought them to mind today?', 'What felt hardest today?', 'What felt slightly lighter?'].map((question) => (
              <button
                key={question}
                onClick={() => setCheckInData({ ...checkInData, heartReflection: question })}
                style={{
                  padding: '1rem',
                  backgroundColor: checkInData.heartReflection === question ? '#FFB6C1' : '#1a1a1a',
                  border: `1px solid ${checkInData.heartReflection === question ? '#FFB6C1' : '#333'}`,
                  borderRadius: '8px',
                  color: checkInData.heartReflection === question ? '#000' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {question}
              </button>
            ))}
          </div>

          {checkInData.heartReflection && (
            <>
              <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
                Healing Actions
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                {['Write what you wish you could say', 'Write what you\'re letting go of today', 'Choose emotional rest', 'Reduce tasks tomorrow'].map((action) => (
                  <button
                    key={action}
                    onClick={() => setCheckInData({ ...checkInData, heartAction: action })}
                    style={{
                      padding: '1rem',
                      backgroundColor: checkInData.heartAction === action ? '#FFB6C1' : '#1a1a1a',
                      border: `1px solid ${checkInData.heartAction === action ? '#FFB6C1' : '#333'}`,
                      borderRadius: '8px',
                      color: checkInData.heartAction === action ? '#000' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    {action}
                  </button>
                ))}
              </div>

              {checkInData.heartAction && (
                <>
                  <div style={{
                    backgroundColor: '#FFB6C11a',
                    border: '1px solid #FFB6C1',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '2rem',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: '#FFB6C1', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                      Your heart heals at its own pace.
                    </p>
                  </div>

                  <button
                    onClick={handleSubmitCheckIn}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      backgroundColor: '#FFB6C1',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#000',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Saving...' : 'Complete Check-in'}
                  </button>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );

  const renderCheckInScreen = () => {
    if (!activeRecovery) return null;

    // Check if already checked in today
    if (todayCheckIn) {
      return (
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '2rem' }}>✅</div>
          <h2 style={{ fontSize: '1.8rem', marginBottom: '1rem', color: '#FFB6C1' }}>
            You've already checked in today!
          </h2>
          <p style={{ color: '#ccc', marginBottom: '2rem' }}>
            Come back tomorrow to continue your journey.
          </p>
          <button
            onClick={() => setCurrentScreen('summary')}
            style={{
              padding: '1rem 2rem',
              backgroundColor: '#FFB6C1',
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            View Summary
          </button>
        </div>
      );
    }

    switch (activeRecovery.recovery_type) {
      case 'addiction':
        return renderAddictionCheckIn();
      case 'work_stress':
        return renderWorkStressCheckIn();
      case 'academic_stress':
        return renderAcademicCheckIn();
      case 'loss':
        return renderLossCheckIn();
      case 'heartbreak':
        return renderHeartbreakCheckIn();
      default:
        return null;
    }
  };

  const renderSummaryScreen = () => {
    const recoveryType = RECOVERY_TYPES.find(t => t.value === activeRecovery?.recovery_type);
    
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>{recoveryType?.emoji}</div>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
            Recovery support is active
          </h1>
          <p style={{ color: '#999' }}>
            Started {activeRecovery?.started_at ? new Date(activeRecovery.started_at).toLocaleDateString() : ''}
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '3rem' }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '2rem', color: '#FFB6C1', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FFB6C1' }}>{stats.daysActive}</div>
            <div style={{ color: '#999', fontSize: '0.9rem' }}>Days Active</div>
          </div>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faCheck} style={{ fontSize: '2rem', color: '#4ade80', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ade80' }}>{stats.totalCheckIns}</div>
            <div style={{ color: '#999', fontSize: '0.9rem' }}>Total Check-ins</div>
          </div>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faFire} style={{ fontSize: '2rem', color: '#f97316', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f97316' }}>{stats.streak}</div>
            <div style={{ color: '#999', fontSize: '0.9rem' }}>Day Streak</div>
          </div>
        </div>

        {/* What this means */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #FFB6C1',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: '#FFB6C1', marginBottom: '1.5rem' }}>What this means:</h3>
          <ul style={{ color: '#ccc', lineHeight: '2', paddingLeft: '1.5rem' }}>
            <li>Your workload adapts to your capacity</li>
            <li>Pressure is reduced</li>
            <li>You are allowed to move slowly</li>
          </ul>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'grid', gap: '1rem' }}>
          <button
            onClick={() => setCurrentScreen('check_in')}
            style={{
              padding: '1rem',
              backgroundColor: '#FFB6C1',
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Continue recovery
          </button>
          <button
            onClick={() => {
              setCurrentScreen('select_type');
            }}
            style={{
              padding: '1rem',
              backgroundColor: '#1a1a1a',
              border: '1px solid #FFB6C1',
              borderRadius: '8px',
              color: '#FFB6C1',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Adjust support
          </button>
          <button
            onClick={handleDeactivate}
            style={{
              padding: '1rem',
              backgroundColor: '#1a1a1a',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Exit recovery
          </button>
        </div>
      </div>
    );
  };

  if (loading && currentScreen === 'welcome') {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ fontSize: '1.5rem', color: '#FFB6C1' }}>Loading recovery space...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '2rem' }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        {/* Render current screen */}
        {currentScreen === 'welcome' && renderWelcomeScreen()}
        {currentScreen === 'select_type' && renderSelectTypeScreen()}
        {currentScreen === 'addiction_habit' && renderAddictionHabitScreen()}
        {currentScreen === 'check_in' && renderCheckInScreen()}
        {currentScreen === 'summary' && renderSummaryScreen()}
      </div>
    </div>
  );
};

export default Recovery;
